"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { setAuthToken, getAuthToken } from "@/lib/api";
import {
  LogOut,
  FileText,
  MessageSquare,
  Calendar,
  LayoutDashboard,
  Wallet,
  Landmark,
  Menu,
  X,
  Sparkles,
  Upload as UploadIcon,
} from "lucide-react";
import { ClientFeaturesProvider, useClientFeatures, FeatureName } from "@/lib/client-features";

const AUTH_PAGES = ["/client/login", "/client/register", "/client/forgot-password"];

interface NavItemDef {
  href: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  exact: boolean;
  feature: FeatureName;
}

const NAV_ITEMS: NavItemDef[] = [
  { href: "/client/dashboard", labelKey: "components.clientPortal.navUpload", icon: UploadIcon, exact: true, feature: "upload" },
  { href: "/client/invoices", labelKey: "components.clientPortal.navInvoices", icon: FileText, exact: true, feature: "invoices" },
  { href: "/client/transactions", labelKey: "components.clientPortal.navTransactions", icon: FileText, exact: true, feature: "banking" },
  { href: "/client/bank-connections", labelKey: "components.clientPortal.navBanks", icon: Landmark, exact: true, feature: "banking" },
  { href: "/client/tickets", labelKey: "components.clientPortal.navTickets", icon: MessageSquare, exact: false, feature: "tickets" },
  { href: "/client/appointments", labelKey: "components.clientPortal.navAppointments", icon: Calendar, exact: true, feature: "appointments" },
  { href: "/client/credits", labelKey: "components.clientPortal.navCredits", icon: Wallet, exact: true, feature: "credits" },
];

function ClientHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations();
  const isInIframe = typeof window !== "undefined" && window.self !== window.top;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { has } = useClientFeatures();
  const visibleNavItems = NAV_ITEMS.filter((it) => has(it.feature));

  const handleLogout = async () => {
    setAuthToken(null);
    await signOut({ redirect: false });
    router.push("/client/login");
  };

  const isActive = (item: typeof NAV_ITEMS[0]) =>
    item.exact ? pathname === item.href : pathname.startsWith(item.href);

  return (
    <header className="glass border-b border-border/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Left: Logo + Badge */}
          <div className="flex items-center gap-3 shrink-0">
            <Link href="/client/dashboard" className="flex items-center h-full shrink-0">
              {!isInIframe && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src="/billance-ledger-logo.png"
                  alt="Billance Ledger"
                  className="h-10 sm:h-12 w-auto shrink-0"
                />
              )}
            </Link>
            <span className="hidden sm:inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              {t('components.clientPortal.badge')}
            </span>
          </div>

          {/* Center: Nav (desktop) */}
          <nav className="hidden md:flex items-center gap-1">
            {visibleNavItems.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-3.5 py-2 text-sm font-medium rounded-full flex items-center gap-2 transition-all duration-200 ${
                    active
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </Link>
              );
            })}
          </nav>

          {/* Right: Language + Logout + Mobile toggle */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="icon" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('components.clientPortal.logout')}</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? t('common.close') : t('nav.expand')}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden pb-4 pt-2 border-t border-border/50">
            <nav className="flex flex-col gap-1">
              {visibleNavItems.map((item) => {
                const active = isActive(item);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center gap-3 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const [isReady, setIsReady] = useState(false);

  const isAuthPage = AUTH_PAGES.some((p) => pathname.startsWith(p));

  // Support token-from-hash for iframe embedding (widget integration)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#token=")) {
      const token = hash.slice(7);
      if (token) {
        setAuthToken(token);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }
    // Listen for token messages from parent (widget)
    const handleTokenMessage = (event: MessageEvent) => {
      if (event.data?.type === "BOOKKEEPING_WIDGET_TOKEN" && event.data.token) {
        setAuthToken(event.data.token);
      }
    };
    window.addEventListener("message", handleTokenMessage);
    return () => window.removeEventListener("message", handleTokenMessage);
  }, []);

  // Send resize messages to parent iframe (if embedded)
  useEffect(() => {
    if (window.self === window.top) return; // not in iframe
    const sendHeight = () => {
      window.parent.postMessage(
        { type: "BOOKKEEPING_WIDGET_RESIZE", height: document.documentElement.scrollHeight },
        "*"
      );
    };
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    sendHeight();
    return () => observer.disconnect();
  }, [isReady]);

  useEffect(() => {
    if (status === "authenticated" && session?.backendAccessToken) {
      setAuthToken(session.backendAccessToken);
      setIsReady(true);
    } else if (status === "unauthenticated") {
      const existingToken = getAuthToken();
      if (existingToken) {
        setIsReady(true);
      } else {
        setAuthToken(null);
        if (!isAuthPage) {
          router.push("/client/login");
        } else {
          setIsReady(true);
        }
      }
    } else if (status === "loading") {
      const existingToken = getAuthToken();
      if (existingToken) {
        setIsReady(true);
      }
    }
  }, [status, session, router, isAuthPage]);

  if (isAuthPage) {
    return <>{children}</>;
  }

  if (status === "loading" && !isReady) {
    return <ClientLoadingScreen />;
  }

  // Allow rendering when we have a backend auth_token even if there's no
  // NextAuth session (e.g. just after accepting an invite, or when embedded
  // as a widget via #token=...). Without this check, status === "unauthenticated"
  // would short-circuit and render a blank screen for token-only sessions.
  if (status === "unauthenticated" && !isReady) {
    return null;
  }

  return (
    <ClientFeaturesProvider>
      <div className="min-h-screen bg-background">
        <ClientHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">{children}</main>
      </div>
    </ClientFeaturesProvider>
  );
}

function ClientLoadingScreen() {
  const t = useTranslations();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-[oklch(0.60_0.22_290)] flex items-center justify-center animate-pulse">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <p className="text-sm text-muted-foreground">{t('components.clientPortal.loading')}</p>
      </div>
    </div>
  );
}
